import { useMemo, useRef, useState } from "react";
import Sheet from "../Sheet.jsx";
import EmptyState from "../EmptyState.jsx";
import GridStatus from "../GridStatus.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import { InlineMetricGrid } from "../PageHero.jsx";
import HierarchyMap from "./HierarchyMap.jsx";
import RegisterReadings from "./RegisterReadings.jsx";
import { CompositionPanel } from "./Composition.jsx";
import { EYEBROW_CLASS, NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import {
  dailyByGroup,
  forgetRegisterReading,
  groupName,
  groupOf,
  levelDots,
  openRegisterFile,
  pathOf,
  readCoverage,
  subtreeReads,
  useRegisterSnapshot,
} from "../../lib/registerSnapshot.js";

// RegisterAnatomy: the organization read as a work register: which groups of
// people work together, the areas of work each group owns, the people, kinds of
// work and tools behind each, and the norms and intents a register build read
// out of their sessions.
//
// One selection drives the page. The map, the group chips, the breadcrumb and
// the Groups/Areas column all select a node; everything below follows it. The
// reading is organization-wide, so the map always starts at the organization,
// whatever scope the lens was opened on.

const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);

function nodeName(R, id) {
  const n = R.nodes[id];
  if (!n) return id;
  if (n.id === "org") return R.meta.org_label || "Organization";
  if (n.kind === "region") return groupName(R.groups.find((g) => g.id === id) || { id });
  return n.label;
}

function Swatch({ color, size = 8 }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block shrink-0 rounded-[2px]"
      style={{ width: size, height: size, backgroundColor: color || "var(--line-strong)" }}
    />
  );
}

// -- the map ---------------------------------------------------------------------------------------------------------

function GroupChips({ R, selectedGroup, onSelect }) {
  return (
    <div className="flex flex-wrap gap-[6px]">
      {R.groups.map((g) => {
        const on = selectedGroup === g.id;
        return (
          <button
            key={g.id}
            type="button"
            aria-pressed={on}
            onClick={() => onSelect(g.id)}
            className={[
              "inline-flex max-w-[280px] min-w-0 items-center gap-[6px] rounded-[2px] border px-[7px] py-[3px] text-left text-[11px] cursor-pointer",
              on
                ? "border-[color:var(--green)] bg-[color:var(--green-soft)]"
                : "border-[color:var(--line)] bg-[color:var(--control-bg)] hover:border-[color:var(--line-strong)]",
            ].join(" ")}
          >
            <Swatch color={R.hue.get(g.id)} />
            <span className="shrink-0 font-medium text-[color:var(--ink)]">{g.id}</span>
            <span className="min-w-0 truncate text-[color:var(--muted)]">{g.descriptor || "no areas"}</span>
            <span className="shrink-0 tabular-nums text-[color:var(--muted-soft)]">{g.people}</span>
          </button>
        );
      })}
    </div>
  );
}

function SharedAreas({ R, onSelect }) {
  const shared = [...R.shared].filter((id) => R.nodes[id]);
  if (!shared.length) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-[10px] gap-y-[4px] text-[11px]">
      <span className={EYEBROW_CLASS}>Shared across groups</span>
      {shared.map((id) => {
        const n = R.nodes[id];
        const byGroup = Object.entries(n.people_by_group || {}).sort((a, b) => b[1] - a[1]);
        return (
          <HoverTooltip
            key={id}
            wide
            placement="top"
            label={`${n.label}: ${byGroup.map(([g, k]) => `${g} ${k}`).join(", ")} people`}
          >
            <button
              type="button"
              onClick={() => onSelect(id)}
              className="inline-flex items-center gap-[5px] text-[color:var(--ink)] hover:text-[color:var(--green)] cursor-pointer"
            >
              <span className="inline-flex overflow-hidden rounded-[1px]" aria-hidden="true">
                {byGroup.map(([g, k]) => (
                  <span key={g} style={{ width: Math.max(3, k * 3), height: 7, backgroundColor: R.hue.get(g) }} />
                ))}
              </span>
              {n.label}
            </button>
          </HoverTooltip>
        );
      })}
    </div>
  );
}

function MapSheet({ R, source, kept, sel, onSelect, scopedElsewhere }) {
  const m = R.meta;
  const describe = (band) => {
    const n = R.nodes[band.id];
    if (!n) return null;
    const people = n.people?.length ?? 0;
    const owner = n.kind === "area" ? `, ${groupOf(R.nodes, n.id)}` : "";
    return { title: nodeName(R, n.id), detail: `${people} ${people === 1 ? "person" : "people"}, ${formatCompact(n.sessions)} sessions${owner}` };
  };
  const window = m.window_start && m.window_end ? `${m.window_start.slice(0, 10)} to ${m.window_end.slice(0, 10)}` : null;
  return (
    <Sheet
      eyebrow="Work register"
      title={`${m.people} people in ${R.groups.length} groups`}
      detail={[
        `Groups are people whose sessions share content; areas are the bodies of work a group owns. Width is people for groups and sessions for areas.`,
        window ? ` Read from ${formatCompact(m.sessions_read)} of ${formatCompact(m.sessions)} agent sessions, ${window}.` : "",
        scopedElsewhere ? " The reading covers the whole organization, so the map starts there." : "",
        source
          ? kept
            ? ` Opened from ${source}; this browser keeps it for your account and never uploads it.`
            : ` Opened from ${source}; it stays in this tab and is not uploaded.`
          : "",
      ].join("")}
      actions={source ? (
        <button
          type="button"
          onClick={() => { forgetRegisterReading(); }}
          className="h-[24px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] text-[11px] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] cursor-pointer"
        >
          Forget this reading
        </button>
      ) : null}
    >
      <HierarchyMap
        tree={R.tree}
        activity={R.activity}
        selectedId={sel}
        onSelect={onSelect}
        shareOf={R.shareOf}
        hueOf={R.hueOf}
        describe={describe}
        ariaLabel="Work register map: the organization, its groups, and their areas. Select a band to read it."
        emptyLabel="The register has no groups yet."
      />
      <GroupChips R={R} selectedGroup={groupOf(R.nodes, sel)} onSelect={onSelect} />
      <SharedAreas R={R} onSelect={onSelect} />
    </Sheet>
  );
}

// -- the focused node ---------------------------------------------------------------------------------------------------

function Breadcrumb({ R, id, onSelect }) {
  const path = pathOf(R.nodes, id);
  return (
    <nav aria-label="Where this sits" className="flex min-w-0 flex-wrap items-center gap-[4px] text-[11px]">
      {path.map((n, i) => {
        const last = i === path.length - 1;
        const label = n.id === "org" ? R.meta.org_label || "Organization" : n.kind === "region" ? n.id : n.label;
        return (
          <span key={n.id} className="inline-flex min-w-0 items-center gap-[4px]">
            {last ? (
              <span className={`${EYEBROW_CLASS} truncate`}>{label}</span>
            ) : (
              <button type="button" onClick={() => onSelect(n.id)} className={`${EYEBROW_CLASS} truncate hover:text-[color:var(--green)] cursor-pointer`}>
                {label}
              </button>
            )}
            {last ? null : <span aria-hidden="true" className="text-[color:var(--muted-soft)]">/</span>}
          </span>
        );
      })}
    </nav>
  );
}

// Sessions (or another unit) per day, stacked by group in the map's hues. Hover names the day.
export function DayBars({ R, node, unit = "sessions" }) {
  const rows = useMemo(() => dailyByGroup(node, R.days, R.groupIds, groupOf(R.nodes, node.id)), [node, R]);
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...rows.map((r) => r.total));
  const dated = rows.filter((r) => r.total > 0).length;
  if (!dated) return <span className={NOTE_CLASS}>No dated {unit}.</span>;
  const W = 6;
  const H = 64;
  const peak = rows.reduce((best, r) => (r.total > best.total ? r : best), rows[0]);
  return (
    <div className="grid min-w-0 gap-[3px]">
      <div className="flex items-baseline justify-between gap-[8px]">
        <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{unit} per day</span>
        <span className={`${NOTE_CLASS} text-[10px] tabular-nums`}>peak {formatCompact(peak.total)} on {peak.day.slice(5)}</span>
      </div>
      <svg
        viewBox={`0 0 ${rows.length * W} ${H}`}
        preserveAspectRatio="none"
        className="block h-[64px] w-full"
        role="img"
        aria-label={`${unit[0].toUpperCase()}${unit.slice(1)} per day, ${rows[0].day} to ${rows[rows.length - 1].day}, peak ${peak.total} on ${peak.day}.`}
        onMouseLeave={() => setHover(null)}
      >
        <line x1="0" x2={rows.length * W} y1={H - 0.5} y2={H - 0.5} stroke="var(--plot-ink-soft)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        {rows.map((r, i) => {
          let y = H;
          return (
            <g key={r.day}>
              {r.parts.map((p) => {
                const h = (p.n / max) * (H - 2);
                y -= h;
                return <rect key={p.group || "all"} x={i * W + 0.8} y={y} width={W - 1.6} height={h} fill={R.hue.get(p.group) || "var(--green)"} opacity={hover && hover.i !== i ? 0.45 : 0.9} />;
              })}
              <rect
                x={i * W}
                y={0}
                width={W}
                height={H}
                fill="transparent"
                onMouseEnter={(e) => setHover({ i, rect: e.currentTarget.getBoundingClientRect() })}
              />
            </g>
          );
        })}
      </svg>
      <div className={`${NOTE_CLASS} flex justify-between text-[10px] tabular-nums`}>
        <span>{rows[0].day.slice(5)}</span>
        <span>{rows[rows.length - 1].day.slice(5)}</span>
      </div>
      {/* Mounted only while a bar is hovered: an anchorRect tooltip given a
          null rect keeps its last position and would draw an empty frame. */}
      {hover ? (
        <HoverTooltip
          anchorRect={hover.rect}
          placement="top"
          wide
          label={
            <span className="grid gap-[2px]">
              <span className="font-medium">{rows[hover.i].day}: {formatCompact(rows[hover.i].total)} {unit}</span>
              {node.daily?.by_group ? rows[hover.i].parts.map((p) => (
                <span key={p.group} className="flex items-center gap-[6px]">
                  <Swatch color={R.hue.get(p.group)} size={7} />
                  {p.group} {formatCompact(p.n)}
                </span>
              )) : null}
            </span>
          }
        />
      ) : null}
    </div>
  );
}

// Who did the acts the register observed: the agent on its own, a person, or an automation.
export function WhoActed({ actors }) {
  const parts = [
    ["The agent", actors?.agent || 0, 0.8],
    ["A person", actors?.person || 0, 0.45],
    ["Automation", (actors?.automation || 0) + (actors?.system || 0), 0.22],
  ];
  const total = parts.reduce((a, p) => a + p[1], 0);
  if (!total) return null;
  return (
    <div className="grid gap-[4px]">
      <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Who acted</span>
      <div className="flex h-[7px] w-full gap-[2px] overflow-hidden" role="img" aria-label={parts.map(([l, n]) => `${l} ${pct(n, total)}%`).join(", ")}>
        {parts.filter((p) => p[1] > 0).map(([label, n, a]) => (
          <span key={label} className="h-full rounded-[1px]" style={{ width: `${(n / total) * 100}%`, backgroundColor: "var(--ink)", opacity: a }} />
        ))}
      </div>
      <div className={`${NOTE_CLASS} flex flex-wrap gap-x-[10px] text-[10px]`}>
        {parts.map(([label, n]) => (
          <span key={label} className="tabular-nums">{label} {pct(n, total)}%</span>
        ))}
      </div>
    </div>
  );
}

function entries(rows) {
  return { entries: rows, total: rows.length };
}

function FocusSheet({ R, id, onSelect }) {
  const n = R.nodes[id];
  const org = R.nodes.org;
  const sessions = n.sessions || 0;
  const kids = (n.children || []).filter((c) => R.nodes[c]);
  const reads = subtreeReads(R.nodes, id);
  const cov = readCoverage(R.nodes, id);
  const areaCount = n.id === "org" ? Object.values(R.nodes).filter((x) => x.kind === "area").length : kids.length;

  // Where: groups at the organization, areas in a group, and inside an area its own sub-areas or subjects.
  const where = n.id === "org"
    ? {
        title: "Groups",
        rows: R.groups.map((g) => ({ id: g.id, label: groupName(g), activity: R.nodes[g.id]?.sessions || 0, share: pct(R.nodes[g.id]?.sessions || 0, sessions) })),
        pick: (gid) => onSelect(gid),
      }
    : kids.length
      ? {
          title: n.kind === "region" ? "Areas" : "Parts",
          rows: kids.map((c) => ({ id: c, label: R.nodes[c].label, activity: R.nodes[c].sessions || 0, share: pct(R.nodes[c].sessions || 0, sessions) }))
            .sort((a, b) => b.activity - a.activity),
          pick: (cid) => onSelect(cid),
        }
      : {
          title: "Subjects",
          rows: (n.top_subjects || []).map((s) => ({ id: s.subject, label: s.subject, activity: s.sessions || 0, share: pct(s.sessions || 0, sessions) })),
          pick: undefined,
        };
  const people = (n.people || []).map((p) => ({
    id: p.label,
    label: p.dept ? `${p.label} (${p.dept})` : p.label,
    activity: p.sessions || 0,
    share: pct(p.sessions || 0, sessions),
    group: p.group,
  }));
  const maxPoints = Math.max(1, ...(n.kinds_of_work || []).map((k) => k.points || 0));
  const kinds = (n.kinds_of_work || []).map((k) => ({
    id: k.label,
    label: k.label,
    activity: k.points || 0,
    // At the organization every kind of work happens "here", so the bar compares kinds; below it the bar is the
    // share of that kind's work that happens in this node.
    share: n.id === "org" ? pct(k.points || 0, maxPoints) : Math.round((k.share || 0) * 100),
  }));
  const maxAct = Math.max(1, ...(n.acts || []).map(([, v]) => v));
  const acts = (n.acts || []).map(([act, v]) => ({ id: act, label: act, activity: v, share: pct(v, maxAct) }));
  const tools = (n.services || []).map((s) => ({ id: s.service, label: s.service, activity: s.sessions || 0, share: pct(s.sessions || 0, sessions) }));
  const toolLift = n.id === "org" ? undefined : new Map((n.services || []).map((s) => [s.service, s.lift]));

  const groupId = groupOf(R.nodes, id);
  const onPerson = (pid) => {
    const p = people.find((x) => x.id === pid);
    if (p?.group && R.nodes[p.group]) onSelect(p.group);
  };
  const facts = [
    `${people.length} ${people.length === 1 ? "person" : "people"}`,
    `${formatCompact(sessions)} sessions`,
    n.id === "org" ? null : `${pct(sessions, org.sessions)}% of the organization's sessions`,
    n.kind === "area" && groupId ? `owned by ${groupId}` : null,
    R.shared.has(id) ? "shared by several groups" : null,
  ].filter(Boolean).join(", ");

  return (
    <Sheet
      eyebrow={<Breadcrumb R={R} id={id} onSelect={onSelect} />}
      title={
        <span className="inline-flex min-w-0 items-center gap-[8px]">
          {groupId ? <Swatch color={R.hue.get(groupId)} size={10} /> : null}
          <span className="min-w-0 truncate">{nodeName(R, id)}</span>
        </span>
      }
      detail={facts}
    >
      {/* Three full-width bands: the numbers, the days, then who and what. The
          columns take the number strip's tracks and cell inset (its 12px padding
          plus 1px divider), so each one starts under a number. */}
      <InlineMetricGrid
        items={[
          ["People read", `${cov.read} of ${cov.members}`, cov.outside ? `and ${cov.outside} from other groups` : "a session each, read closely"],
          ["Sessions read", formatCompact(reads.length), `of ${formatCompact(sessions)}`],
          [n.kind === "area" && !kids.length ? "Subjects" : "Areas", formatCompact(n.kind === "area" && !kids.length ? n.subjects || 0 : areaCount), n.cross_group_subjects ? `${n.cross_group_subjects} subjects cross groups` : "bodies of shared work"],
          ["Tools", formatCompact((n.services || []).length), n.id === "org" ? "used across the organization" : "more used here than in sibling nodes"],
        ]}
      />
      <div className="pt-[6px]">
        <DayBars R={R} node={n} />
      </div>
      <div className="grid gap-y-[14px] pt-[6px] [grid-template-columns:repeat(4,minmax(0,1fr))] max-[1100px]:[grid-template-columns:repeat(2,minmax(0,1fr))] [&>*]:pr-2.5 [&>*+*]:pl-[13px] max-[1100px]:[&>*:nth-child(odd)]:pl-0">
        <CompositionPanel title="People" data={entries(people)} scopeTotal={sessions} rows={8} onDrill={onPerson} />
        <CompositionPanel title={where.title} data={entries(where.rows)} scopeTotal={sessions} rows={8} onDrill={where.pick} />
        <div className="grid content-start gap-[12px]">
          <CompositionPanel title="Kinds of work" data={entries(kinds)} rows={5} />
          <CompositionPanel title="What agents did" data={entries(acts)} rows={5} />
          <WhoActed actors={n.actors} />
        </div>
        <CompositionPanel title="Tools and services" data={entries(tools)} scopeTotal={sessions} rows={8} liftById={toolLift} />
      </div>
    </Sheet>
  );
}

// -- norms, intents, tensions ---------------------------------------------------------------------------------------------

function Dots({ level, kind }) {
  const { filled, of } = levelDots(level, kind);
  return (
    <span className="inline-flex gap-[2px]" aria-hidden="true">
      {Array.from({ length: of }, (_, i) => (
        <span key={i} className="h-[5px] w-[5px] rounded-full" style={{ backgroundColor: i < filled ? "var(--ink)" : "var(--line-strong)" }} />
      ))}
    </span>
  );
}

// Where an item holds: one cell per group, strength by how often that group's windows showed it.
function GroupStrip({ R, counts }) {
  const max = Math.max(1, ...Object.values(counts || {}));
  const seen = R.groupIds.filter((g) => counts?.[g]);
  return (
    <span
      className="inline-grid gap-[2px]"
      style={{ gridTemplateColumns: `repeat(${R.groupIds.length}, 11px)` }}
      role="img"
      aria-label={seen.length ? `Seen in ${seen.map((g) => `${g} (${counts[g]})`).join(", ")}` : "Not seen in a group's own windows"}
    >
      {R.groupIds.map((g) => (
        <span
          key={g}
          className="h-[11px] rounded-[1px]"
          style={counts?.[g]
            ? { backgroundColor: R.hue.get(g), opacity: 0.35 + 0.65 * Math.sqrt(counts[g] / max) }
            : { backgroundColor: "var(--line-soft)" }}
        />
      ))}
    </span>
  );
}

function StripHead({ R }) {
  return (
    <span className="grid gap-[3px]" aria-hidden="true">
      <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Seen in group</span>
      <span className="inline-grid gap-[2px] text-center text-[9px] leading-none text-[color:var(--muted-soft)]" style={{ gridTemplateColumns: `repeat(${R.groupIds.length}, 11px)` }}>
        {R.groupIds.map((g) => <span key={g}>{g.replace(/^R/, "")}</span>)}
      </span>
    </span>
  );
}

function ItemRow({ R, item, kind }) {
  const counter = item.counter
    ? `${kind === "intent" ? "Working against this looks like" : "Deviation looks like"}: ${item.counter}`
    : null;
  return (
    <div role="listitem" className="grid items-start gap-x-[12px] border-t border-[color:var(--line-soft)] py-[7px] first:border-t-0 [grid-template-columns:96px_minmax(0,1fr)_auto]">
      <span className="grid gap-[3px] pt-[3px]">
        <Dots level={item.level} kind={kind} />
        <span className={`${NOTE_CLASS} whitespace-nowrap text-[10px] tabular-nums`}>{item.level}, {item.people ?? 0}</span>
      </span>
      {counter ? (
        <HoverTooltip wide maxWidth={420} placement="top" label={counter}>
          <span className="text-[12px] leading-[1.38] text-[color:var(--ink)]" tabIndex={0}>{item.statement}</span>
        </HoverTooltip>
      ) : (
        <span className="text-[12px] leading-[1.38] text-[color:var(--ink)]">{item.statement}</span>
      )}
      <span className="pt-[3px]"><GroupStrip R={R} counts={item.groups} /></span>
    </div>
  );
}

function NormsSheet({ R, id }) {
  const n = R.nodes[id];
  const p = n.profile || { norms: [], intents: [] };
  const [tab, setTab] = useState("norms");
  const list = tab === "norms" ? p.norms || [] : p.intents || [];
  const kind = tab === "norms" ? "norm" : "intent";
  const along = pathOf(R.nodes, id).map((x) => (x.id === "org" ? "the organization" : x.kind === "region" ? x.id : x.label));
  return (
    <Sheet
      eyebrow="How work is done here"
      title={n.id === "org" ? "The organization's norms" : `Norms for ${n.kind === "region" ? n.id : n.label}`}
      detail={`${(p.norms || []).length} norms and ${(p.intents || []).length} intents${along.length > 1 ? `, composed along ${along.join(" / ")}` : ""}. Hover one to see what a deviation looks like.`}
      actions={
        <div className="flex gap-[2px]" role="tablist" aria-label="Norms or intents">
          {[["norms", "Norms"], ["intents", "Intents"]].map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={[
                "h-[24px] rounded-[2px] border px-[8px] text-[11px] cursor-pointer",
                tab === k ? "border-[color:var(--line-strong)] bg-[color:var(--bg)] text-[color:var(--ink)]" : "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)]",
              ].join(" ")}
            >
              {label}
            </button>
          ))}
        </div>
      }
    >
      {list.length ? (
        <div className="grid">
          <div className="grid items-end gap-x-[12px] pb-[4px] [grid-template-columns:96px_minmax(0,1fr)_auto]">
            <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{tab === "norms" ? "Prevalence, people" : "Weight, people"}</span>
            <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{tab === "norms" ? "Norm" : "Intent"}</span>
            <StripHead R={R} />
          </div>
          <div role="list">
            {list.map((item) => <ItemRow key={item.ref} R={R} item={item} kind={kind} />)}
          </div>
        </div>
      ) : (
        <EmptyState title={tab === "norms" ? "No norms read here" : "No intents read here"} note="This node's sessions were not read closely enough to state how its work is done." />
      )}
    </Sheet>
  );
}

function TensionsSheet({ R, id }) {
  const n = R.nodes[id];
  const scope = n.id === "org" ? null : groupOf(R.nodes, id);
  const items = R.tensions.filter((t) => (scope ? t.node === scope : true));
  return (
    <Sheet
      eyebrow="Where the register found disagreement"
      title={items.length ? `${items.length} ${items.length === 1 ? "tension" : "tensions"}` : "No recorded tensions"}
      detail={scope
        ? `Conflicts and concerns recorded while rolling up ${scope}'s norms.`
        : "Conflicts between groups' norms, and practices flagged as concerning, recorded while rolling the register up."}
    >
      {/* Balanced columns, as many as fit: a long list stays a short band. */}
      <div role="list" className="columns-[300px] gap-x-[24px]">
        {items.map((t, i) => (
          <div key={i} role="listitem" className="grid break-inside-avoid gap-[3px] border-t border-[color:var(--line-soft)] py-[7px]">
            <span className="flex items-center gap-[6px] text-[10px] uppercase tracking-[0.06em]">
              <span aria-hidden="true" className="inline-flex h-[12px] w-[12px] items-center justify-center" style={{ color: t.kind === "conflict" ? "var(--ink)" : "var(--severity-medium)" }}>
                {t.kind === "conflict" ? (
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1.5 4h8M7.5 1.8 9.7 4 7.5 6.2M10.5 8h-8M4.5 5.8 2.3 8l2.2 2.2" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 1.4 11 10.4H1z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /><path d="M6 4.6v2.7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /><circle cx="6" cy="8.8" r=".7" fill="currentColor" /></svg>
                )}
              </span>
              <span className="text-[color:var(--eyebrow)]">{t.kind === "conflict" ? "Conflict" : "Concern"}</span>
              {scope ? null : <span className="text-[color:var(--muted-soft)]">{t.node === "org" ? "organization" : t.node}</span>}
            </span>
            <span className="text-[12px] leading-[1.38] text-[color:var(--ink)]">{t.text}</span>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// -- the lens ----------------------------------------------------------------------------------------------------------

// Picks a register build's lens data from disk. The file is read in the tab and
// held in memory only (lib/registerSnapshot), so no served build is needed.
function OpenReadingAction() {
  const input = useRef(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState(null);
  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setReading(true);
    setError(null);
    const opened = await openRegisterFile(file);
    if (opened.status !== "ready") {
      setReading(false);
      setError(opened.error);
    }
  };
  return (
    <div className="grid justify-items-start gap-[6px]">
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={reading}
        className="h-[26px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[9px] text-[10px] leading-[1.08] tracking-[0.04em] uppercase font-medium text-[color:var(--ink)] cursor-pointer disabled:cursor-default disabled:opacity-60"
      >
        {reading ? "Reading the file..." : "Open a register reading"}
      </button>
      <input
        ref={input}
        type="file"
        accept="application/json,.json"
        className="sr-only"
        aria-label="Choose a register reading file"
        onChange={choose}
      />
      {error ? <span role="alert" className="text-[11px] text-red-400">{error}</span> : null}
    </div>
  );
}

export default function RegisterAnatomy({ scopePath = [] }) {
  const snap = useRegisterSnapshot();
  const [sel, setSel] = useState("org");
  if (snap.status === "loading") {
    return (
      <section aria-busy="true" className="h-[220px] border border-[color:var(--line)] bg-[color:var(--panel-soft)]">
        <GridStatus state="loading" label="Loading the register reading" />
      </section>
    );
  }
  if (snap.status !== "ready") {
    return (
      <EmptyState
        title="No register reading yet"
        note={snap.status === "error"
          ? `The register reading could not be loaded (${snap.error}). Reload to try again, or open one from a file.`
          : "Register builds are not served to the dashboard yet. Open a build's lens data (a .json file) to read it here. The file is read in this browser and never uploaded."}
        action={<OpenReadingAction />}
      />
    );
  }
  const R = snap.data;
  if (R.kind === "readings") {
    return <RegisterReadings R={R} scopePath={scopePath} source={snap.source} kept={snap.kept} />;
  }
  const id = R.nodes[sel] ? sel : "org";
  return (
    <div className="grid gap-[12px]">
      <MapSheet R={R} source={snap.source} kept={snap.kept} sel={id} onSelect={setSel} scopedElsewhere={scopePath.length > 0} />
      <FocusSheet R={R} id={id} onSelect={setSel} />
      <NormsSheet R={R} id={id} />
      <TensionsSheet R={R} id={id} />
    </div>
  );
}

